import React, { cloneElement } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  PasswordInput,
  SelectArrayInput,
  ReferenceArrayInput,
  EditContextProvider,
  Title,
  useEditController,
  AutocompleteArrayInput,
  FormDataConsumer,
  BooleanInput,
} from "react-admin";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import { Grid, Typography } from "@material-ui/core";
import CustomEditToolbar from "components/CustomEditToolbar";
import SanitizedGrid from "components/SanitizedGrid";
import { emailValidation } from "utils/validation";
import _ from "lodash";
import { labelStyle } from "utils/style";
import EmailInput from "../../components/EmailInput";

const EditForm = (props: any) => {
  const controllerProps = useEditController(props);
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    redirect, // the default redirection route. Defaults to 'list'
    resource, // the resource name, deduced from the location. e.g. 'posts'
    save, // the update callback, to be passed to the underlying form as submit handler
    saving, // boolean that becomes true when the dataProvider is called to update the record
    version, // integer used by the refresh feature
    defaultTitle,
  } = controllerProps;

  const formatRecord = (res: any) => {
    if (res) {
      if (res.roles) {
        const temp = _.cloneDeep(res.roles);
        const roleIds = temp.map((item: any) => {
          return item ? item.id : item;
        });
        res.roleIds = roleIds;

        if (res.roleIds.includes(1)) {
          res.isSuperAdmin = true;
        }

        if (res.admin?.schools) {
          const temp = _.cloneDeep(res.admin.schools);
          const schoolIds = temp.map((item: any) => {
            return item ? item.id : item;
          });
          res.schoolIds = schoolIds;
        }
      }
    }
    return res;
  };

  return (
    <EditContextProvider value={controllerProps}>
      <div>
        <Typography variant="h4" style={labelStyle}>
          {record?.email}
        </Typography>

        <Title title={defaultTitle} />

        {loaded &&
          cloneElement(props.children, {
            className: "MuiPaper-root MuiPaper-elevation1",
            basePath,
            record: formatRecord(record),
            redirect,
            resource,
            save,
            saving,
            version,
          })}
      </div>
    </EditContextProvider>
  );
};

const UserEdit = (props: any) => {
  const { id } = props;

  const breadcrumb = [
    { link: "/users", title: "Users" },
    { link: `/users/${id}`, title: id },
  ];

  const transform = async (data: any) => {
    if (data.password === "") {
      delete data.password;
    }

    if (data.isSuperAdmin) {
      data.roleIds = [1];
      data.schoolIds = [];
    }

    return data;
  };

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <EditForm {...props} transform={transform} undoable={false}>
        <SimpleForm toolbar={<CustomEditToolbar isShowDelete={false} />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={4}>
              <EmailInput label="Email" source="email" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <PasswordInput source="password" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <BooleanInput source="isSuperAdmin" label="Set As Super Admin" />
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData, ...rest }) =>
                !formData.isSuperAdmin && (
                  <>
                    <Grid item xs={4}>
                      <ReferenceArrayInput
                        label="Roles"
                        source="roleIds"
                        reference="roles"
                        filter={{ IsOnlyForAdmin: true }}
                        validate={required()}
                      >
                        <SelectArrayInput optionText="name" />
                      </ReferenceArrayInput>
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={4}>
                      <ReferenceArrayInput
                        label="Schools"
                        source="schoolIds"
                        reference="schools"
                        validate={required()}
                        filterToQuery={(searchText: any) => {
                          if (searchText !== "") return { search: searchText };
                        }}
                      >
                        <AutocompleteArrayInput optionText="name" />
                      </ReferenceArrayInput>
                    </Grid>
                    <Grid item xs={8}></Grid>
                  </>
                )
              }
            </FormDataConsumer>
          </SanitizedGrid>
        </SimpleForm>
      </EditForm>
    </>
  );
};

export default UserEdit;
